import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import i18n from "../i18n";
import {
  api,
  setAccessToken,
  setMsspScopeOverride,
  setTenantScopeOverride,
} from "./api";
import {
  getMe,
  patchLocale,
  postLogin,
  postLogout,
  postRefresh,
} from "@ward3/frontend-shared/authApi";
import { useAuthEvents } from "@ward3/frontend-shared/hooks/useAuthEvents";

const AuthContext = createContext(null);

// Phase 2b2 — derive the hierarchical scope from a v2 token. v1 tokens
// (`role_v2` absent) fall back to `{ kind: "tenant" }` with `null` ids
// — the backend will resolve that to the default tenant via
// `Claims::base_scope`. Returning `null` triggers a re-login.
function deriveScopeFromClaims(claims) {
  const r = claims.role_v2 ?? null;
  if (r === "platform_admin" || r === "platform_ops") {
    return { kind: "platform", platform_id: claims.platform_id ?? null };
  }
  if (r === "mssp_admin" || r === "mssp_analyst") {
    return { kind: "mssp", mssp_id: claims.mssp_id ?? null };
  }
  // tenant_* OR legacy v1 token → fallback tenant scope.
  return {
    kind: "tenant",
    mssp_id: claims.mssp_id ?? null,
    tenant_id: claims.tenant_id ?? null,
  };
}

export function AuthProvider({ children }) {
  // Phase 2b2 — `user` now carries the hierarchical fields read from
  // the JWT. Legacy fields (`sub`, `role`, `exp`) stay so existing
  // modules that read `user.role === "admin"` keep working until they
  // are migrated to `user.role_v2`.
  const [user, setUser] = useState(null);
  // `scopeOverride` is the effective narrowing set by the user via the
  // ScopeSelector (only meaningful for mssp_*/platform_*). Null = stay
  // on the JWT-anchored scope. Stored as `{ mssp_id, tenant_id }` with
  // either side possibly null; api.js translates to headers.
  const [scopeOverride, setScopeOverrideState] = useState({
    mssp_id: null,
    tenant_id: null,
  });
  const [ready, setReady] = useState(false);

  // Décode le JWT et hydrate la moitié immédiatement disponible des
  // champs user. `email` + `locale` n'étant pas dans le token (claims
  // immuables jusqu'au refresh, cf. plan i18n), ils sont récupérés
  // ensuite par `fetchMe()`.
  const decodeAndSet = useCallback((token) => {
    try {
      const c = jwtDecode(token);
      setUser({
        sub: c.sub,
        role: c.role,
        exp: c.exp,
        role_v2: c.role_v2 ?? null,
        platform_id: c.platform_id ?? null,
        mssp_id: c.mssp_id ?? null,
        tenant_id: c.tenant_id ?? null,
        scope: deriveScopeFromClaims(c),
        // Marqués `undefined` jusqu'à la résolution de /auth/me.
        email: undefined,
        locale: undefined,
      });
      setAccessToken(token);
    } catch {
      setUser(null);
      setAccessToken(null);
    }
  }, []);

  // Fetch /auth/me pour enrichir le user avec email + locale (non
  // portés par le JWT). Appelé après chaque `decodeAndSet` réussi.
  // Best-effort : un échec n'invalide pas la session — l'utilisateur
  // peut s'authentifier, juste sans i18n côté serveur (fallback
  // localStorage / EN).
  const fetchMe = useCallback(async () => {
    try {
      const data = await getMe(api);
      setUser((u) => (u ? { ...u, email: data.email, locale: data.locale } : u));
    } catch {
      // /auth/me peut échouer si le token a juste expiré entre refresh
      // et /me ; l'event `anm:auth-expired` traitera le cleanup.
    }
  }, []);

  // Handler de session expirée + sync i18n (déportés dans
  // `frontend-shared/hooks/useAuthEvents` #17-A). Note : on ne reset
  // PAS i18n.language ici. Le visiteur garde sa préférence
  // (localStorage) sur l'écran de login.
  const handleExpired = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setMsspScopeOverride(null);
    setTenantScopeOverride(null);
    setScopeOverrideState({ mssp_id: null, tenant_id: null });
  }, []);
  useAuthEvents({ i18n, locale: user?.locale, onExpired: handleExpired });

  // On mount: try silent refresh (if refresh cookie present).
  useEffect(() => {
    (async () => {
      try {
        const data = await postRefresh(api);
        decodeAndSet(data.access_token);
        await fetchMe();
      } catch {
        // not logged in — fine
      } finally {
        setReady(true);
      }
    })();
  }, [decodeAndSet, fetchMe]);

  // Phase 2b2 — login accepts optional mssp_slug / tenant_slug. The
  // frontend resolves them from the subdomain (Login.jsx) and passes
  // through unchanged. Same-origin login does not need them and the
  // backend treats `undefined` as "any MSSP/tenant" (= legacy lookup).
  const login = useCallback(
    async (email, password, opts = {}) => {
      const data = await postLogin(api, email, password, opts);
      decodeAndSet(data.access_token);
      await fetchMe();
      return data;
    },
    [decodeAndSet, fetchMe],
  );

  // Met à jour la préférence de locale côté serveur + côté i18next.
  // Source de vérité : le retour de PATCH /auth/me/preferences (peut
  // différer de l'input si validation server-side rejette).
  const updateLocale = useCallback(async (locale) => {
    const data = await patchLocale(api, locale);
    setUser((u) => (u ? { ...u, locale: data.locale } : u));
    // L'effet sur user.locale s'occupe de `i18n.changeLanguage`, mais
    // on l'appelle aussi ici pour que le switcher soit instantané sans
    // attendre un re-render React.
    if (i18n.language !== data.locale) i18n.changeLanguage(data.locale);
    return data.locale;
  }, []);

  const logout = useCallback(async () => {
    await postLogout(api);
    setUser(null);
    setAccessToken(null);
    setMsspScopeOverride(null);
    setTenantScopeOverride(null);
    setScopeOverrideState({ mssp_id: null, tenant_id: null });
  }, []);

  // Phase 2b2 — set/update the scope override. Validation is
  // server-side (require_scope_context middleware will reject any
  // override that escapes the claim's scope), so we keep the client
  // side dumb on purpose: it just echoes the override into the headers.
  const setScopeOverride = useCallback(({ mssp_id, tenant_id }) => {
    setMsspScopeOverride(mssp_id ?? null);
    setTenantScopeOverride(tenant_id ?? null);
    setScopeOverrideState({
      mssp_id: mssp_id ?? null,
      tenant_id: tenant_id ?? null,
    });
  }, []);

  const value = {
    user,
    ready,
    login,
    logout,
    scopeOverride,
    setScopeOverride,
    updateLocale,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
